// QueBoard (깨보드) Constants — 레이아웃 자동 계산 (깨구단2 참고)

import type { CharacterId } from './types'

// Fonts (CookieRun CDN)
export const FONT_CDN_BASE = 'https://d2ql3aoebfyj3b.cloudfront.net/game/QuebonLight/font'
export const FONTS = {
  regular: 'CookeiRunRegular',
  bold: 'CookeiRunBold',
  black: 'CookeiRunBlack',
} as const

// Grid dimensions
export const MAP_SIZE = 10
export const BANK_COLS = 4
export const BANK_ROWS = 3
export const BANK_EMPTY_CELL = { col: 3, row: 2 } // [3][2] is always empty

// Cell sizing (px) — 레거시 53×53 프레임 기준
export const CELL_SIZE = 53
export const CELL_GAP = 2
export const BOUNDARY_GAP = 8 // extra gap every 5 cells

// Side indicator sizing
export const SIDE_SIZE = 40
export const SIDE_GAP = 2

// === LEFT PANEL (문제/정답/은행) ===
export const LEFT_PANEL_X = 20 // 패널 배경 → 컨테이너 간격
export const LEFT_PANEL_WIDTH = 374 // 기존 358 + 브라운바 16 흡수
export const LEFT_PANEL_TOTAL = LEFT_PANEL_X + LEFT_PANEL_WIDTH // 패널 전체 너비 (394)

// 컨테이너 내부 콘텐츠 영역
const CONTAINER_PAD = 16 // 컨테이너 → 콘텐츠 내부 패딩
export const CONTENT_X = LEFT_PANEL_X + CONTAINER_PAD // 콘텐츠 시작 X (30)
export const CARD_WIDTH = LEFT_PANEL_TOTAL - CONTENT_X * 2 // 카드 너비 (334)

// Problem + Answer 통합 카드
export const PROBLEM_OFFSET_X = CONTENT_X
export const PROBLEM_OFFSET_Y = LEFT_PANEL_X + CONTAINER_PAD // 30
export const PROBLEM_CARD_HEIGHT = 175

// 하위 호환: TutorialOverlay 'mode-selector' highlight 용
export const ANSWER_OFFSET_X = CONTENT_X
export const ANSWER_OFFSET_Y = PROBLEM_OFFSET_Y + PROBLEM_CARD_HEIGHT / 2
export const ANSWER_HEIGHT = PROBLEM_CARD_HEIGHT / 2
export const MODE_SELECTOR_X = ANSWER_OFFSET_X
export const MODE_SELECTOR_Y = ANSWER_OFFSET_Y
export const MODE_SELECTOR_HEIGHT = ANSWER_HEIGHT

// Bank
export const BANK_OFFSET_X = CONTENT_X
export const BANK_OFFSET_Y = PROBLEM_OFFSET_Y + PROBLEM_CARD_HEIGHT + 14 // ~209

// Bank height
export const BANK_HEIGHT = 207

// === RIGHT PANEL (그리드 + 표시등 + 텔레포트 버튼) ===
export const GRID_GAP = 40
export const MAP_OFFSET_X = LEFT_PANEL_WIDTH + GRID_GAP + SIDE_SIZE + 10 // 464 (기존과 동일)
export const MAP_OFFSET_Y = PROBLEM_OFFSET_Y + SIDE_SIZE + 6 // 표시등 상단을 문제 카드와 정렬 (~66)

// Grid edges (calculated)
export const GRID_BOTTOM = MAP_OFFSET_Y + MAP_SIZE * (CELL_SIZE + CELL_GAP) + BOUNDARY_GAP // ~664
export const GRID_RIGHT = MAP_OFFSET_X + MAP_SIZE * (CELL_SIZE + CELL_GAP) + BOUNDARY_GAP // ~1070

// === Stage dimensions (자동 계산) ===
export const STAGE_WIDTH = GRID_RIGHT + 18
export const STAGE_HEIGHT = GRID_BOTTOM + 18

// === Character Themes (캐릭터별 색상) ===
export const CHARACTER_THEMES: Record<Exclude<CharacterId, 'random'>, {
  rightBg: string   // 우측 패널 배경색
  panelA: string    // 체커보드 밝은색 (이미지 fallback)
  panelB: string    // 체커보드 어두운색 (이미지 fallback)
  unitOn: string    // on 상태 fallback 색
  unitDone: string  // done 상태 fallback 색
}> = {
  queda:  { rightBg: '#FFA9A9', panelA: '#FFE8EE', panelB: '#FFCCD8', unitOn: '#FF8FAB', unitDone: '#FFC1CC' },
  chaeli: { rightBg: '#FFD56E', panelA: '#FFF8DC', panelB: '#FFE9A0', unitOn: '#FFD54F', unitDone: '#FFE082' },
  ddami:  { rightBg: '#76BBFF', panelA: '#E8F4FF', panelB: '#C4DEFF', unitOn: '#64B5F6', unitDone: '#90CAF9' },
}

// Colors (레거시 깨구단2 참고)
export const COLORS = {
  // Unit states (이미지 사용 시 fallback)
  unitOff: '#C8A96E',
  unitOn: '#FFB74D',
  unitSelect: '#FF7043',
  unitDone: '#A5D6A7',

  // Side indicator states
  sideOff: '#D4B896',
  sideHalf: '#FFD54F',
  sideFull: '#66BB6A',

  // Drag overlay
  dragTie: 'rgba(76, 175, 80, 0.3)',
  dragBorrow: 'rgba(255, 152, 0, 0.3)',
  dragPlace: 'rgba(33, 150, 243, 0.3)',
  dragReturn: 'rgba(156, 39, 176, 0.3)',

  // UI
  background: '#F5E6C8',
  gridBorder: '#C8A96E',
  bankBackground: '#E8D5B0',
  panelA: '#FAEBD7',
  panelB: '#F5DEB3',
  leftPanel: '#06063F',
  problemCard: '#19195B',
  bankCard: '#2F2F77',
  resetButton: '#E53935',
  textPrimary: '#3E2723',
  textSecondary: '#6D4C41',
  buttonActive: '#7B1FA2',
  buttonInactive: '#BCAAA4',
  correct: '#4CAF50',
  incorrect: '#F44336',

  // Teleport manual mode
  teleportGuide: 'rgba(255, 213, 79, 0.25)',       // selecting 가이드 (연한 노랑)
  teleportSelect: 'rgba(255, 152, 0, 0.5)',         // selecting 선택됨 (진한 주황)
  teleportGhostValid: 'rgba(76, 175, 80, 0.5)',     // placing 유효 (초록 반투명)
  teleportGhostInvalid: 'rgba(244, 67, 54, 0.4)',   // placing 무효 (빨간 반투명)
  teleportCancelBtn: '#78909C',                      // 취소 버튼
} as const

// Drag mode config
export const DRAG_MODES = {
  tie: { name: 'tie', label: '묶기', color: COLORS.dragTie },
  borrow: { name: 'borrow', label: '꺼내기', color: COLORS.dragBorrow },
  place: { name: 'place', label: '놓기', color: COLORS.dragPlace },
  return: { name: 'return', label: '반납', color: COLORS.dragReturn },
} as const

// Teleport highlight positions (텔레포트 = 표시등 자체, 튜토리얼 하이라이트용)
export const TELEPORT_BTN_SIZE = SIDE_SIZE
export const TELEPORT_BTN_TOP_Y = MAP_OFFSET_Y - SIDE_SIZE - 6
// 가로5: "5" 표시등 (index 4)
export const TELEPORT_TOP_5_X = MAP_OFFSET_X + 4 * (CELL_SIZE + CELL_GAP) + (CELL_SIZE - SIDE_SIZE) / 2
// 가로10: "십" 표시등 (index 9)
export const TELEPORT_TOP_10_X = MAP_OFFSET_X + 9 * (CELL_SIZE + CELL_GAP) + BOUNDARY_GAP + (CELL_SIZE - SIDE_SIZE) / 2
// 좌측: y-side 표시등 열
export const TELEPORT_BTN_LEFT_X = MAP_OFFSET_X - SIDE_SIZE - 6
// 세로5: "5" 표시등 (index 4)
export const TELEPORT_LEFT_5_Y = MAP_OFFSET_Y + 4 * (CELL_SIZE + CELL_GAP) + (CELL_SIZE - SIDE_SIZE) / 2
// 세로10: "십" 표시등 (index 9)
export const TELEPORT_LEFT_10_Y = MAP_OFFSET_Y + 9 * (CELL_SIZE + CELL_GAP) + BOUNDARY_GAP + (CELL_SIZE - SIDE_SIZE) / 2

// Tutorial panel (은행 하단, bank.png와 동일 width)
export const TUTORIAL_OFFSET_X = BANK_OFFSET_X
export const TUTORIAL_OFFSET_Y = BANK_OFFSET_Y + BANK_HEIGHT + 14
export const TUTORIAL_WIDTH = CARD_WIDTH
export const TUTORIAL_HEIGHT = 120

// Long press threshold for side indicator release (ms)
export const LONG_PRESS_DURATION = 500
